import { createContext, useContext, useState, useEffect, useCallback } from "react";
import type { CurrentUser } from "@/mocks/auth";
import { api, setAccessToken } from "@/api/client";

interface AuthContextValue {
  user: CurrentUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  quickLogin: (email: string) => Promise<boolean>;
  logout: () => Promise<void>;
  switchUser: (email: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => false,
  quickLogin: async () => false,
  logout: async () => {},
  switchUser: async () => false,
});

export function useAuth() {
  return useContext(AuthContext);
}

function mapMeToCurrentUser(me: any): CurrentUser {
  // Backend likely returns role as a compact value (e.g. admin/government_officer/org_user/viewer)
  // while frontend expects exact labels used by navConfig/mocks.
  const backendRole: string = String(me.role ?? "");

  const roleMap: Record<string, CurrentUser["role"]> = {
    "admin": "Admin",
    "government_officer": "Government Health Officer",
    "government health officer": "Government Health Officer",
    "org_user": "Organization User",
    "organization_user": "Organization User",
    "organization user": "Organization User",
    "viewer": "Viewer",
    "read_only_viewer": "Viewer",
    "read-only_viewer": "Viewer",
    // If backend already returns the label, keep it.
    "Admin": "Admin",
    "Government Health Officer": "Government Health Officer",
    "Organization User": "Organization User",
    "Viewer": "Viewer",
  };

  const mappedRole = roleMap[backendRole] ?? (backendRole as CurrentUser["role"]);

  return {
    id: String(me.id ?? ""),
    name: String(me.name ?? ""),
    email: String(me.email ?? ""),
    role: mappedRole,
    organizationId: String(me.organizationId ?? ""),
    organizationName: String(me.organizationName ?? me.organization_name ?? ""),
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load access token (if present) and hydrate /auth/me
  useEffect(() => {
    let cancelled = false;

    async function hydrate() {
      try {
        const savedToken = localStorage.getItem("mg_access_token");
        if (savedToken) setAccessToken(savedToken);

        // Try to get user; if expired, attempt silent refresh
        let me = null;
        if (savedToken) {
          try {
            me = await api.me();
          } catch (err: any) {
            if (err?.status === 401) {
              const refreshToken = localStorage.getItem("mg_refresh_token");
              if (refreshToken) {
                try {
                  const renewed = await api.refresh({ refresh_token: refreshToken });
                  localStorage.setItem("mg_access_token", renewed.access_token);
                  setAccessToken(renewed.access_token);
                  me = await api.me();
                } catch {
                  localStorage.removeItem("mg_access_token");
                  localStorage.removeItem("mg_refresh_token");
                  setAccessToken(null);
                }
              } else {
                localStorage.removeItem("mg_access_token");
                setAccessToken(null);
              }
            } else {
              throw err;
            }
          }
        }

        if (me && !cancelled) {
          setUser(mapMeToCurrentUser(me));
        }
      } catch {
        localStorage.removeItem("mg_access_token");
        localStorage.removeItem("mg_refresh_token");
        setAccessToken(null);
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    hydrate();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    try {
      const token = await api.login({ email, password });
      localStorage.setItem("mg_access_token", token.access_token);
      localStorage.setItem("mg_refresh_token", token.refresh_token);
      setAccessToken(token.access_token);
      const me = await api.me();
      setUser(mapMeToCurrentUser(me));
      return true;
    } catch {
      return false;
    }
  }, []);

  // Backend doesn't support quick login by email in the current API.
  // Keep the UI working by returning false.
  const quickLogin = useCallback(async (_email: string): Promise<boolean> => {
    return false;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // ignore
    } finally {
      localStorage.removeItem("mg_access_token");
      localStorage.removeItem("mg_refresh_token");
      setAccessToken(null);
      setUser(null);
    }
  }, []);

  const switchUser = useCallback(async (_email: string): Promise<boolean> => {
    return false;
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    quickLogin,
    logout,
    switchUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

